<template>
  <div class="t-app__container">
    <heading :title="$t('subscribe')" />
    <div class="h-section h-section--no-margin-top">
      <div class="h-grid h-grid--justify-content-center">
        <div class="h-grid__col h-grid__col--sm-10 h-grid__col--md-8">
          <s-form v-if="!accountCreated" @submit="createUserAccount">
            <p class="m-form-group">
              <label class="a-form-label" for="name">{{ $t('label-name') }}</label>
              <input class="a-form-control" id="name" v-model.trim="$v.name.$model" />
              <span v-if="$v.name.$dirty">
                <span class="m-form-group__error" v-if="!$v.name.required">{{ $t('error-field-required') }}</span>
              </span>
            </p>
            <p class="m-form-group">
              <label class="a-form-label" for="email">{{ $t('label-email') }}</label>
              <input class="a-form-control" id="email" @change="setEmail($event.target.value)" />
              <span v-if="$v.email.$dirty">
                <span class="m-form-group__error" v-if="!$v.email.required">{{ $t('error-field-required') }}</span>
                <span class="m-form-group__error" v-if="!$v.email.email">{{ $t('error-field-not-email') }}</span>
              </span>
            </p>
            <p class="m-form-group">
              <label class="a-form-label" for="phoneNumber">{{ $t('label-phone-number') }}</label>
              <input class="a-form-control" id="phoneNumber" v-model.lazy="$v.phoneNumber.$model" autocomplete="phone-number" />
              <span v-if="$v.phoneNumber.$dirty">
                <span class="m-form-group__error" v-if="!$v.phoneNumber.required">{{ $t('error-field-required') }}</span>
                <span class="m-form-group__error" v-if="!$v.phoneNumber.isPhone">{{ $t('error-phone-field-format') }}</span>
              </span>
            </p>
            <p class="m-form-group">
              <label class="a-form-label" for="password">
                {{ $t('label-password') }}
                <span class="m-form-group__note m-form-group__note--small"
                  >{{ $t('user-password-rules') }}
                  <ul>
                    <li>{{ $t('user-password-rule-1') }}</li>
                    <li>{{ $t('user-password-rule-2') }}</li>
                    <li>{{ $t('user-password-rule-3') }}</li>
                  </ul>
                </span>
              </label>
              <input type="password" class="a-form-control" id="password" :value="password" @change="setPassword($event.target.value)" />
              <span v-if="$v.password.$dirty">
                <span class="m-form-group__error" v-if="!$v.password.required">{{ $t('error-field-required') }}</span>
                <span class="m-form-group__error" v-if="!$v.password.isValidPassword">
                  {{ $t('error-create-password-invalid') }}
                </span>
              </span>
            </p>
            <p class="m-form-group">
              <label class="a-form-label" for="password-confirmation">{{ $t('label-password-confirmation') }}</label>
              <input type="password" class="a-form-control" id="password-confirmation" :value="passwordConfirmation" @change="setPasswordConfirmation($event.target.value)" />
              <span v-if="$v.passwordConfirmation.$dirty">
                <span class="m-form-group__error" v-if="!$v.passwordConfirmation.required">{{ $t('error-field-required') }}</span>
                <span class="m-form-group__error" v-if="!$v.passwordConfirmation.isSamePassword">
                  {{ $t('error-password-confirmation-invalid') }}
                </span>
              </span>
            </p>

            <p class="m-form-group">
              <subscribe-user-heard-about v-model="heardAbout"></subscribe-user-heard-about>
            </p>

            <div class="m-form-ctrl">
              <div class="m-form-ctrl__item">
                <button type="submit" class="a-btn-primary">{{ $t('button-confirm') }}</button>
              </div>
              <div class="m-form-ctrl__item">
                <router-link :to="{ name: $consts.urls.URL_ACCOUNT_LOGIN }">
                  {{ $t('return-btn') }}
                </router-link>
              </div>
            </div>
          </s-form>
          <p class="m-form-group--center" v-else>
            {{ $t('success-create-account', { email: `${email}` }) }}
          </p>
        </div>
      </div>
    </div>

    <div class="t-app__confetti t-app__confetti--01"></div>
    <div class="t-app__dot t-app__dot--large"></div>
    <div class="t-app__confetti t-app__confetti--02"></div>
    <div class="t-app__confetti t-app__confetti--03"></div>
    <div class="t-app__confetti t-app__confetti--04"></div>
    <div class="t-app__dot t-app__dot--medium"></div>
    <div class="t-app__confetti t-app__confetti--05"></div>
  </div>
</template>

<script>
import SForm from '@/components/form/form'
import Heading from '@/components/heading'
import SubscribeUserHeardAbout from '@/components/form/subscribe-user-heard-about'

import UserService from '@/services/user'
import Notification from '@/services/notification'
import LoaderService from '@/services/loader'

import { required, email } from 'vuelidate/lib/validators'

import FormHelper from '@/helpers/form'
import StatusHelper from '@/helpers/status'

const isValidPassword = value => FormHelper.isValidPassword(value)
const isSamePassword = function(value) {
  return this.password === value
}
const isPhone = value => FormHelper.isPhone(value)

export default {
  components: {
    SForm,
    Heading,
    SubscribeUserHeardAbout
  },
  data() {
    return {
      name: '',
      email: '',
      phoneNumber: '',
      password: '',
      passwordConfirmation: '',
      accountCreated: false,
      heardAbout: ''
    }
  },
  validations: {
    name: {
      required
    },
    email: {
      required,
      email
    },
    phoneNumber: {
      required,
      isPhone
    },
    password: {
      required,
      isValidPassword
    },
    passwordConfirmation: {
      required,
      isSamePassword
    }
  },
  methods: {
    createUserAccount: async function() {
      this.$v.$touch()
      if (!this.$v.$invalid) {
        LoaderService.showLoader()
        const response = await UserService.createUserAccount(this.name, this.email, this.phoneNumber, this.password, this.heardAbout)
        LoaderService.hideLoader()
        if (StatusHelper.isSuccess(response.status)) {
          this.accountCreated = true
        } else if (response.status === 400) {
          Notification.showError(this.$t('email-alreadyused'))
        } else {
          Notification.showError(this.$t('error-create-account'))
        }
      }
    },
    setEmail: function(value) {
      this.email = value
      this.$v.email.$touch()
    },
    setPassword: function(value) {
      this.password = value
      this.$v.password.$touch()
    },
    setPasswordConfirmation: function(value) {
      this.passwordConfirmation = value
      this.$v.passwordConfirmation.$touch()
    },
    getHeardAbout: async function(value) {
      this.heardAbout = value
    }
  }
}
</script>
